import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  X, LockKey, Keyboard, Fingerprint, CheckCircle, ShieldCheck, Trash,
} from "@phosphor-icons/react";
import {
  startRegistration,
} from "@simplewebauthn/browser";
import { staffApi, formatStaffErr, getDeviceId } from "../../lib/staffApi";
import { useStaffAuth } from "../../lib/staffAuth";

/**
 * Staff security settings — bottom-sheet modal for
 *   · setting / changing the account password
 *   · setting / changing the 4-digit device PIN
 *   · enrolling this device for biometric (WebAuthn platform authenticator)
 *
 * All actions require the user to already be signed in (this modal only
 * opens from inside the app once authenticated).
 */
export default function StaffSecuritySettings({ onClose }) {
  const { staff } = useStaffAuth();
  const [device, setDevice] = useState({ has_password: false, has_pin: false, has_biometric: false });
  const [tab, setTab] = useState("pin"); // pin | password | biometric
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [pw, setPw] = useState("");
  const [pwCur, setPwCur] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (!staff?.phone) return;
    try {
      const r = await staffApi.post("/mobile-auth/device/status", {
        user_type: "staff", identifier: staff.phone, device_id: getDeviceId(),
      });
      setDevice({
        has_password: !!r.data.has_password,
        has_pin: !!r.data.has_pin,
        has_biometric: !!r.data.has_biometric,
      });
    } catch { /* ignore */ }
  }, [staff]);
  useEffect(() => { refresh(); }, [refresh]);

  const savePin = async () => {
    if (!/^\d{4}$/.test(pin)) { toast.error("PIN must be 4 digits"); return; }
    if (pin !== confirmPin) { toast.error("PINs don't match"); return; }
    setBusy(true);
    try {
      await staffApi.post("/mobile-auth/pin/set", {
        device_id: getDeviceId(), pin,
      });
      toast.success("PIN saved");
      setPin(""); setConfirmPin("");
      refresh();
    } catch (e) { toast.error(formatStaffErr(e)); }
    setBusy(false);
  };

  const savePassword = async () => {
    if (pw.length < 6) { toast.error("Password must be 6+ chars"); return; }
    setBusy(true);
    try {
      await staffApi.post("/mobile-auth/password/set", {
        new_password: pw,
        current_password: device.has_password ? pwCur : undefined,
      });
      toast.success("Password saved");
      setPw(""); setPwCur("");
      refresh();
    } catch (e) { toast.error(formatStaffErr(e)); }
    setBusy(false);
  };

  const enrolBiometric = async () => {
    if (!window.PublicKeyCredential) {
      toast.error("WebAuthn is not supported on this browser");
      return;
    }
    setBusy(true);
    try {
      const chal = await staffApi.post("/mobile-auth/biometric/register/challenge", {
        device_id: getDeviceId(),
      });
      const options = typeof chal.data.options === "string"
        ? JSON.parse(chal.data.options) : chal.data.options;
      const attResp = await startRegistration({ optionsJSON: options });
      await staffApi.post("/mobile-auth/biometric/register/verify", {
        challenge_id: chal.data.challenge_id,
        credential: attResp,
        device_id: getDeviceId(),
      });
      toast.success("Biometric enrolled on this device");
      refresh();
    } catch (e) {
      const msg = e?.name === "NotAllowedError"
        ? "Enrollment cancelled or not supported"
        : formatStaffErr(e) || e?.message || "Enrollment failed";
      toast.error(msg);
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/50 flex items-end sm:items-center justify-center" onClick={onClose}
          data-testid="staff-security-modal">
      <div className="bg-white w-full sm:max-w-md sm:rounded-lg rounded-t-2xl p-5 max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780]">Security</div>
            <div className="font-heading text-lg inline-flex items-center gap-1.5">
              <ShieldCheck size={16} />PIN, password & biometric
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-[#F7F6F2]">
            <X size={16} />
          </button>
        </div>

        {/* Status pills */}
        <div className="grid grid-cols-3 gap-1.5 mb-4" data-testid="staff-sec-status">
          <StatusPill Icon={Keyboard} label="PIN" ok={device.has_pin} />
          <StatusPill Icon={LockKey} label="Password" ok={device.has_password} />
          <StatusPill Icon={Fingerprint} label="Biometric" ok={device.has_biometric} />
        </div>

        <div className="grid grid-cols-3 gap-1 bg-[#F7F6F2] rounded-md p-1 mb-4">
          <TabBtn active={tab === "pin"} onClick={() => setTab("pin")} Icon={Keyboard} label="PIN" test="tab-pin"/>
          <TabBtn active={tab === "password"} onClick={() => setTab("password")} Icon={LockKey} label="Password" test="tab-password"/>
          <TabBtn active={tab === "biometric"} onClick={() => setTab("biometric")} Icon={Fingerprint} label="Biometric" test="tab-biometric"/>
        </div>

        {tab === "pin" && (
          <div className="space-y-3" data-testid="staff-sec-pin-panel">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780] block mb-1">
                {device.has_pin ? "New 4-digit PIN" : "Set a 4-digit PIN"}
              </label>
              <input data-testid="sec-pin" value={pin} inputMode="numeric" maxLength={4}
                      onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                      placeholder="••••"
                      className="w-full h-12 px-3 border border-[#E5E2D9] rounded-md text-center text-[18px] font-mono tracking-[0.4em]"/>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780] block mb-1">Confirm PIN</label>
              <input data-testid="sec-pin-confirm" value={confirmPin} inputMode="numeric" maxLength={4}
                      onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                      placeholder="••••"
                      className="w-full h-12 px-3 border border-[#E5E2D9] rounded-md text-center text-[18px] font-mono tracking-[0.4em]"/>
            </div>
            <button onClick={savePin} disabled={busy || pin.length !== 4 || pin !== confirmPin}
                     data-testid="sec-pin-save"
                     className="w-full h-11 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-50 text-white rounded-md text-sm font-medium">
              {busy ? "Saving…" : device.has_pin ? "Update PIN" : "Save PIN"}
            </button>
            <div className="text-[10px] text-[#8A8780]">
              5 wrong attempts locks the PIN for 15 min. Losing this device won&apos;t leak your account.
            </div>
          </div>
        )}

        {tab === "password" && (
          <div className="space-y-3" data-testid="staff-sec-password-panel">
            {device.has_password && (
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780] block mb-1">Current password</label>
                <input data-testid="sec-pw-current" type="password" value={pwCur}
                        onChange={(e) => setPwCur(e.target.value)}
                        className="w-full h-11 px-3 border border-[#E5E2D9] rounded-md text-sm"/>
              </div>
            )}
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780] block mb-1">
                {device.has_password ? "New password" : "Set a password"}
              </label>
              <input data-testid="sec-pw-new" type="password" value={pw}
                      onChange={(e) => setPw(e.target.value)} placeholder="At least 6 characters"
                      className="w-full h-11 px-3 border border-[#E5E2D9] rounded-md text-sm"/>
            </div>
            <button onClick={savePassword} disabled={busy || pw.length < 6}
                     data-testid="sec-pw-save"
                     className="w-full h-11 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-50 text-white rounded-md text-sm font-medium">
              {busy ? "Saving…" : device.has_password ? "Update password" : "Save password"}
            </button>
          </div>
        )}

        {tab === "biometric" && (
          <div className="space-y-3" data-testid="staff-sec-biometric-panel">
            <div className="bg-[#F7F6F2] rounded-md p-3 text-xs text-[#5C5A55]">
              Enrol this device&apos;s built-in biometric (Face ID, Touch ID, or Android biometric).
              The credential is bound to this device — reuse is impossible from another phone.
            </div>
            <button onClick={enrolBiometric} disabled={busy}
                     data-testid="sec-bio-enroll"
                     className="w-full h-12 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-50 text-white rounded-md text-sm font-medium inline-flex items-center justify-center gap-2">
              <Fingerprint size={18} weight="fill" />
              {busy ? "Waiting…" : device.has_biometric ? "Re-enrol biometric" : "Enrol biometric on this device"}
            </button>
            {device.has_biometric && (
              <div className="text-[11px] text-[#2D5A27] inline-flex items-center gap-1">
                <CheckCircle size={12} weight="fill"/> Biometric already enrolled on this device
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const StatusPill = ({ Icon, label, ok }) => (
  <div className={`rounded-md px-2 py-2 text-center border ${ok ? "bg-[#D4E0D1]/60 border-[#2D5A27]/30 text-[#1A3317]" : "bg-[#F7F6F2] border-[#E5E2D9] text-[#8A8780]"}`}>
    <Icon size={14} weight={ok ? "fill" : "regular"} className="inline"/>
    <div className="text-[9px] uppercase tracking-[0.16em] mt-0.5">{label}</div>
    <div className="text-[10px] font-medium">{ok ? "on" : "off"}</div>
  </div>
);

const TabBtn = ({ active, onClick, Icon, label, test }) => (
  <button onClick={onClick} data-testid={`sec-${test}`}
           className={`h-10 rounded-sm text-[10px] inline-flex flex-col items-center justify-center gap-0.5 ${
             active ? "bg-white shadow-sm text-[#2D5A27]" : "text-[#8A8780] hover:text-[#5C5A55]"
           }`}>
    <Icon size={12} weight={active ? "fill" : "regular"} />
    {label}
  </button>
);
